import { computed, Injectable, signal } from '@angular/core';
import { LiveReportSettings, RoomDetail } from '@shared/models';

/**
 * What the screen keeps of its room, and nothing else: it is opened by a room display, so the
 * room's equipment and wiring are never kept at all - the surest way for them not to leak.
 * It also makes the narrower read the backend still owes us a drop-in.
 */
export interface LiveReportRoom {
  id: string;
  slug: string;
  organizationId: string;
  label: string;
  liveReport: LiveReportSettings;
}

/**
 * The room is written by the guard before the page exists, so the first render already has its
 * appearance - a wall screen must not be seen switching to dark mode once it is up.
 */
@Injectable({ providedIn: 'root' })
export class LiveReportState {
  private readonly _room = signal<LiveReportRoom | undefined>(undefined);

  /** The room currently displayed, `undefined` until the guard has loaded one. */
  readonly room = this._room.asReadonly();

  /** Display settings of that room. The backend always serves them, defaults included. */
  readonly settings = computed(() => this._room()?.liveReport);

  /** Narrowed here and not at the call sites, so a second caller cannot forget it. */
  setRoom({ id, slug, organizationId, label, liveReport }: RoomDetail): void {
    this._room.set({ id, slug, organizationId, label, liveReport });
  }

  /**
   * Writes the display settings of the room already held. The screen shows a change before the
   * API has confirmed it, and puts the previous ones back when it refuses.
   */
  applySettings(liveReport: LiveReportSettings): void {
    this._room.update((room) => (room ? { ...room, liveReport } : room));
  }
}
